import classNames from "classnames";
import { useTranslations } from "next-intl";
import React, { useMemo } from "react";

import type { PaginationProps } from "~/types/pagination";
import { buildPaginationUrl } from "~/utils/pagination-utils";

import styles from "./pagination.module.scss";

/**
 * Pagination component
 *
 * Dual mode:
 * - visible={true}: Full pagination UI (for users with ?page in URL)
 * - visible={false}: Hidden links for crawlers (display: none)
 */
export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  baseUrl,
  preserveFilters = {},
  visible = false,
  windowSize = 1,
  className = "",
}) => {
  const t = useTranslations();

  // Calculate which pages are inside the visible window
  const windowStart = Math.max(2, currentPage - windowSize);
  const windowEnd = Math.min(totalPages - 1, currentPage + windowSize);

  const allPages = useMemo(() => Array.from({ length: totalPages }, (_, i) => i + 1), [totalPages]);

  if (totalPages <= 1) {
    return null;
  }

  const getPageClassName = (page: number) => {
    if (!visible) return undefined;

    const isCurrent = page === currentPage;
    const isFirst = page === 1;
    const isLast = page === totalPages;
    const isInWindow = page >= windowStart && page <= windowEnd;
    const isVisible = isFirst || isLast || isInWindow;

    return classNames(styles.pageLink, {
      [styles.pageLinkCurrent]: isCurrent,
      [styles.pageLinkHidden]: !isVisible,
    });
  };

  return (
    <nav
      aria-label={t("pagination.navigation")}
      className={visible ? classNames(styles.pagination, className) : styles.hidden}
    >
      <ul>
        {/* Previous page link */}
        {currentPage > 1 && (
          <li>
            <a
              href={buildPaginationUrl(baseUrl, currentPage - 1, preserveFilters)}
              className={styles.navLink}
              rel="prev"
              aria-label={t("pagination.goToPrevious")}
            >
              {visible ? "\u2039" : "Previous Page"}
            </a>
          </li>
        )}

        {/* All page links */}
        {allPages.map((page) => {
          const isCurrent = page === currentPage;
          const showEllipsisBefore = visible && page === windowStart && windowStart > 2;
          const showEllipsisAfter = visible && page === windowEnd && windowEnd < totalPages - 1;

          return (
            <React.Fragment key={page}>
              {showEllipsisBefore && (
                <li aria-hidden="true">
                  <span className={styles.ellipsis}>&hellip;</span>
                </li>
              )}
              <li>
                <a
                  href={buildPaginationUrl(baseUrl, page, preserveFilters)}
                  className={getPageClassName(page)}
                  aria-current={isCurrent ? "true" : "false"}
                  aria-label={t.rich("pagination.goToPage", { page }) as string}
                >
                  {page}
                </a>
              </li>
              {showEllipsisAfter && (
                <li aria-hidden="true">
                  <span className={styles.ellipsis}>&hellip;</span>
                </li>
              )}
            </React.Fragment>
          );
        })}

        {/* Next page link */}
        {currentPage < totalPages && (
          <li>
            <a
              href={buildPaginationUrl(baseUrl, currentPage + 1, preserveFilters)}
              className={styles.navLink}
              rel="next"
              aria-label={t("pagination.goToNext")}
            >
              {visible ? "\u203A" : "Next Page"}
            </a>
          </li>
        )}
      </ul>
    </nav>
  );
};
